import { useState } from "react";
import type { FocusEvent } from "react";

import { Icon, Message } from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import { FieldProps } from "./FieldProps";

const getNumericFieldValue = (value: FieldProps["fieldValue"]): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value !== "" && value !== "-") {
    return Number.parseFloat(value);
  }
  return Number.NaN;
};

const NumericalText = ({
  field,
  fieldValue = "",
  updateIdea,
  isEditing,
}: FieldProps) => {
  const { minValue, maxValue } = field;
  const [validEntry, setValidEntry] = useState(true);
  if (!isEditing) {
    return <span>{String(fieldValue ?? "")} </span>;
  }
  const isNumber = (value: unknown) =>
    !fieldValue || !Number.isNaN(parseFloat(String(value)));

  let message: string | null = null;
  if (fieldValue && fieldValue !== "-" && !isNumber(fieldValue)) {
    message = `Enter a number between ${minValue} and ${maxValue}.`;
  } else if (
    minValue !== undefined &&
    getNumericFieldValue(fieldValue) < minValue
  ) {
    message = `Enter a number greater than ${minValue}.`;
  } else if (
    maxValue !== undefined &&
    getNumericFieldValue(fieldValue) > maxValue
  ) {
    message = `Enter a number less than ${maxValue}.`;
  }

  return (
    <div>
      {message && (
        <Message error size="tiny">
          <Icon name="warning sign" />
          {message}
        </Message>
      )}
      <Input
        fluid
        value={fieldValue as string}
        onChange={(_e, { value }) => {
          //Allow for minus sign to be written first
          if (value === "-" || value === "") {
            updateIdea(value, ["templated", field.id]);
            return;
          }
          //Prevent letters and other invalid inputs
          if (!/^[-]?\d+(\.\d*)?$/.test(value)) {
            return;
          }

          const num = parseFloat(value);
          const isValidMin =
            minValue === undefined || !isNumber(minValue) || num >= minValue;
          const isValidMax =
            maxValue === undefined || !isNumber(maxValue) || num <= maxValue;
          if (isValidMin && isValidMax) {
            setValidEntry(true);
          }

          // Needs to allow setting to string locally if it ends with a dot, to allow for decimal input
          // Should not be saved to the server
          if (value.endsWith(".")) {
            updateIdea(value, ["templated", field.id], true);
          } else {
            updateIdea(num, ["templated", field.id]);
          }
        }}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          const num = parseFloat(e.target.value);
          const isValidMin =
            minValue === undefined || !isNumber(minValue) || num >= minValue;
          const isValidMax =
            maxValue === undefined || !isNumber(maxValue) || num <= maxValue;
          setValidEntry(isNumber(num) && isValidMin && isValidMax);
        }}
        readOnly={!isEditing}
        type="text"
        error={!validEntry}
      />
    </div>
  );
};

export default NumericalText;
